import classNames from 'classnames';
import { useState, type CSSProperties } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Marquee } from '#src/atoms/container/Marquee/index.tsx';
import { ScoreTarget } from '#src/atoms/icons/ScoreTarget.tsx';
import { Trophy } from '#src/atoms/icons/Trophy.tsx';
import { BaseAccordion } from '#src/atoms/layout/Accordion/index.tsx';
import { Horizontal, Vertical } from '#src/atoms/layout/flex/index.tsx';
import {
  BodyL,
  BodyM,
  BrandM,
  BrandXXL,
} from '#src/atoms/typography/index.tsx';
import { Img } from '#src/atoms/ui/Img/index.tsx';
import { TextureBackground } from '#src/atoms/ui/TextureBackground/index.tsx';
import { useIsLaptop } from '#src/hooks/device/useIsLaptop.ts';
import { laptopAndAbove } from '#src/style/mediaQuery.ts';

import position1 from './assets/position1.webp';
import scoreTargets from './assets/score-targets.webp';

const Root = styled.section`
  display: flex;
  flex-direction: column-reverse;
  padding: var(--double-unit);
  @media ${laptopAndAbove} {
    flex-direction: row;
    align-items: center;
    gap: calc(var(--unit) * 10);
    min-height: calc(var(--unit) * 94);
    padding: calc(var(--unit) * 10);
    position: relative;
  }
`;

const MarqueeWrapper = styled.div`
  height: calc(var(--unit) * 30);
  mask-image: linear-gradient(to right, transparent, #000 10% 90%, transparent);
  @media ${laptopAndAbove} {
    height: var(--marquee-height);
    overflow: hidden;
  }
`;

const MarqueeImg = styled(Img)`
  height: calc(var(--unit) * 30);
  width: auto;
  @media ${laptopAndAbove} {
    height: var(--marquee-height);
  }
`;

const TextContainer = styled(Vertical).attrs({
  gap: 6,
})`
  justify-content: flex-start;
  min-height: calc(var(--unit) * 61);
  @media ${laptopAndAbove} {
    width: 50%;
  }
`;

const TitleContainer = styled(Vertical).attrs({
  gap: 4,
})`
  z-index: 1;
`;
const AccordionTitle = styled(BrandM).attrs({
  bold: true,
  uppercase: true,
})`
  color: var(--c-nd-400-opaque);
  &.isOpen {
    color: var(--white);
  }
`;

const StyledTextureBackground = styled(TextureBackground)`
  position: absolute;
  inset: 0;
`;

const AssetWrapper = styled.div`
  @media ${laptopAndAbove} {
    width: calc(var(--unit) * 60);
    z-index: 2;
  }
`;

const assets = {
  leaderboards: (
    <MarqueeWrapper style={{ '--marquee-height': '436px' } as CSSProperties}>
      <Marquee duration="15s">
        <MarqueeImg src={position1} alt="" height="436" width="376" />
        <MarqueeImg src={position1} alt="" height="436" width="376" />
        <MarqueeImg src={position1} alt="" height="436" width="376" />
      </Marquee>
    </MarqueeWrapper>
  ),
  'score-targets': <Img src={scoreTargets} alt="" width="352" height="196" />,
} as const;

export const OurCompetitions = () => {
  const [openAccordionId, setOpenAccordionId] = useState<
    'leaderboards' | 'score-targets' | undefined
  >('leaderboards');
  const isLaptopAndAbove = useIsLaptop();

  const handleAccordionChange = (
    accordionId: 'leaderboards' | 'score-targets'
  ) => {
    if (openAccordionId === accordionId) {
      setOpenAccordionId(undefined);
    } else {
      setOpenAccordionId(accordionId);
    }
  };

  return (
    <Root id="about">
      {!!isLaptopAndAbove && (
        <StyledTextureBackground
          background="wave1"
          mode="dark"
          color="var(--c-nd-400-opaque)"
        />
      )}
      {!!isLaptopAndAbove && (
        <AssetWrapper>{assets[openAccordionId ?? 'leaderboards']}</AssetWrapper>
      )}
      <TextContainer>
        <TitleContainer gap={4}>
          <Vertical gap={2}>
            <BrandM uppercase color="var(--c-limited)">
              <FormattedMessage defaultMessage="Ways to Play" />
            </BrandM>
            <BrandXXL uppercase>
              <FormattedMessage defaultMessage="Our Competitions" />
            </BrandXXL>
          </Vertical>
          <BodyL>
            <FormattedMessage
              defaultMessage="When your players perform well in their actual real-life games,
your team earns points and brings you closer to better rewards."
            />
          </BodyL>
        </TitleContainer>
        <BaseAccordion
          expanded={openAccordionId === 'leaderboards'}
          onExpandChange={() => handleAccordionChange('leaderboards')}
          title={
            <Horizontal>
              <Trophy color="var(--c-blue-300)" />
              <AccordionTitle
                className={classNames({
                  isOpen: openAccordionId === 'leaderboards',
                })}
              >
                <FormattedMessage defaultMessage="Leaderboards" />
              </AccordionTitle>
            </Horizontal>
          }
        >
          <BodyM>
            <FormattedMessage
              defaultMessage="Compete against other managers for major rewards as you challenge
for top places."
            />
          </BodyM>
          {!isLaptopAndAbove && (
            <Horizontal center>{assets.leaderboards}</Horizontal>
          )}
        </BaseAccordion>
        <BaseAccordion
          expanded={openAccordionId === 'score-targets'}
          onExpandChange={() => handleAccordionChange('score-targets')}
          title={
            <Horizontal>
              <ScoreTarget color="var(--c-blue-300)" />
              <AccordionTitle
                className={classNames({
                  isOpen: openAccordionId === 'score-targets',
                })}
              >
                <FormattedMessage defaultMessage="Score Targets" />
              </AccordionTitle>
            </Horizontal>
          }
        >
          <Vertical gap={2}>
            <BodyM>
              <FormattedMessage defaultMessage="Hit specific point thresholds to earn guaranteed cash or card rewards." />
            </BodyM>
            {!isLaptopAndAbove && (
              <Horizontal center>{assets['score-targets']}</Horizontal>
            )}
          </Vertical>
        </BaseAccordion>
      </TextContainer>
    </Root>
  );
};
